/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 23-09-2026
 *
 * De scheiding tussen het modelcanvas en het simulatiescherm: een smalle strook die je kunt verslepen.
 * De ng21-tegenhanger van `setResizeEvents` in de AngularJS-hoofdcontroller (golf 4e), met
 * pointer-events in plaats van zes jQuery-bindingen — één set gebeurtenissen voor muis en touch — en de
 * verhouding als percentage op de `SchermindelingService` in plaats van css-objecten in `localStorage`.
 *
 * De strook ligt aan de rand van het simulatiescherm; hoeveel ruimte dat scherm krijgt, rekent het
 * bouwscherm uit (via de `SchermindelingService`). Het slepen meet tegen `#canvasholder`, de gemeenschappelijke ouder van beide schermen.
 */
import {ChangeDetectionStrategy, Component, ElementRef, HostListener, inject} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import {SchermindelingService} from '../../services/schermindeling.service';

@Component({
    selector: 'dl-splitter',
    templateUrl: './dl-splitter.component.html',
    styleUrls: ['./dl-splitter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslatePipe],
    host: {
        '[class.dl-splitter--hori]': "schermindeling.richting() === 'hori'",
        '[class.dl-splitter--verti]': "schermindeling.richting() === 'verti'"
    }
})
export class DlSplitterComponent {

    /**
     * De verdeling van het bouwscherm; deze strook schrijft er het nieuwe percentage in.
     */
    protected readonly schermindeling = inject(SchermindelingService);

    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

    /**
     * De maat van de houder waarbinnen gesleept wordt, vastgesteld bij het begin van de sleep. `null`
     * als er niet gesleept wordt.
     */
    private houder: DOMRect | null = null;

    /**
     * Begint de sleep. `setPointerCapture` houdt de gebeurtenissen bij deze strook, ook als de
     * aanwijzer over een canvas of buiten het venster komt.
     */
    @HostListener('pointerdown', ['$event'])
    protected beginSleep(gebeurtenis: PointerEvent): void {
        const houder = this.host.nativeElement.closest('#canvasholder');
        if (!houder) {
            return;
        }
        this.houder = houder.getBoundingClientRect();
        this.host.nativeElement.setPointerCapture(gebeurtenis.pointerId);
        gebeurtenis.preventDefault();
    }

    /**
     * Rekent de nieuwe verhouding uit. Bij `hori` staat het simulatiescherm rechts, bij `verti` boven —
     * dezelfde kant als in de AngularJS-versie.
     */
    @HostListener('pointermove', ['$event'])
    protected sleep(gebeurtenis: PointerEvent): void {
        const houder = this.houder;
        if (!houder) {
            return;
        }
        const deel = this.schermindeling.richting() === 'hori'
            ? (houder.right - gebeurtenis.clientX) / houder.width
            : (gebeurtenis.clientY - houder.top) / houder.height;
        this.schermindeling.zetSimulatiedeel(deel * 100);
    }

    /**
     * Rondt de sleep af.
     */
    @HostListener('pointerup', ['$event'])
    @HostListener('pointercancel', ['$event'])
    protected eindigSleep(gebeurtenis: PointerEvent): void {
        this.houder = null;
        if (this.host.nativeElement.hasPointerCapture(gebeurtenis.pointerId)) {
            this.host.nativeElement.releasePointerCapture(gebeurtenis.pointerId);
        }
    }
}
